import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  DELETE_PROFESSIONAL_SERVICE,
  GET_PROFESSIONAL_SERVICE,
  GET_PROFESSIONAL_SERVICES
} from '../../graphql/professional-services';

const SERVICE_TYPE_LABELS = {
  ATTORNEY: 'Attorney',
  BOND_ORIGINATOR: 'Bond Originator',
  CONTRACTOR: 'Contractor',
  INSPECTOR: 'Inspector',
  OTHER: 'Other'
};

const formatLabel = (s) => (s || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const formatAddress = (a) => {
  if (!a) return '—';
  const line1 = [a.unitNumber, a.complexName].filter(Boolean).join(', ');
  const line2 = [a.number, a.street].filter(Boolean).join(' ');
  const line3 = [a.suburb, a.city].filter(Boolean).join(', ');
  const line4 = [a.province, a.postalCode].filter(Boolean).join(' ');
  return [line1, line2, line3, line4].filter(Boolean).join(' · ') || '—';
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
  </div>
);

const Section = ({ title, children }) => (
  <section className="bg-white shadow rounded-lg p-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
    {children}
  </section>
);

const ProfessionalServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_PROFESSIONAL_SERVICE, {
    variables: { id },
    fetchPolicy: 'cache-and-network'
  });

  const [deleteService, { loading: deleting }] = useMutation(DELETE_PROFESSIONAL_SERVICE, {
    refetchQueries: [{
      query: GET_PROFESSIONAL_SERVICES,
      variables: { pageIndex: 0, pageSize: 20, orderAsc: true, orderField: 'companyName', filters: {} }
    }],
    onCompleted: () => navigate('/sales/professional-services'),
    onError: (err) => alert(err.message || 'Delete failed')
  });

  const handleDelete = () => {
    if (!data?.professionalService) return;
    if (window.confirm(`Delete ${data.professionalService.companyName}? This cannot be undone.`)) {
      deleteService({ variables: { id } });
    }
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !data?.professionalService) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error ? error.message : 'Professional service not found'}
      </div>
    );
  }

  const svc = data.professionalService;
  const pc = svc.primaryContact || {};
  const perf = svc.performance || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">{svc.companyName}</h1>
          {svc.tradingName && svc.tradingName !== svc.companyName && (
            <p className="text-gray-600">Trading as {svc.tradingName}</p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {SERVICE_TYPE_LABELS[svc.type] || svc.type}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              svc.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
            }`}>
              {svc.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/sales/professional-services"
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </Link>
          <Link
            to={`/sales/professional-services/${id}/edit`}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      <Section title="Primary Contact">
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Name" value={[pc.title, pc.firstName, pc.lastName].filter(Boolean).join(' ')} />
          <Field label="Email" value={pc.email} />
          <Field label="Cellphone" value={pc.cellphone} />
          <Field label="Telephone" value={pc.telephone} />
          <Field label="Fax" value={pc.fax} />
        </dl>
      </Section>

      <Section title="Specializations & Service Areas">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Specializations</dt>
            <dd className="flex flex-wrap gap-2">
              {svc.specializations?.length ? svc.specializations.map((s) => (
                <span key={s} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {formatLabel(s)}
                </span>
              )) : <span className="text-sm text-gray-500">—</span>}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Service Areas</dt>
            <dd className="flex flex-wrap gap-2">
              {svc.serviceAreas?.length ? svc.serviceAreas.map((s) => (
                <span key={s} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-800">
                  {s}
                </span>
              )) : <span className="text-sm text-gray-500">—</span>}
            </dd>
          </div>
        </div>
      </Section>

      <Section title="Address">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Physical" value={formatAddress(svc.physicalAddress)} />
          <Field label="Postal" value={formatAddress(svc.postalAddress)} />
        </dl>
      </Section>

      <Section title="Registration & Compliance">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Registration Number" value={svc.registrationNumber} />
          <Field label="VAT Number" value={svc.vatNumber} />
          <Field label="License Number" value={svc.licenseNumber} />
          <Field label="Professional Body" value={svc.professionalBody} />
          <Field label="Website" value={svc.website ? (
            <a href={svc.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{svc.website}</a>
          ) : null} />
        </dl>
      </Section>

      <Section title="Performance">
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Client Satisfaction" value={perf.clientSatisfaction ? `${perf.clientSatisfaction.toFixed(1)} / 5` : null} />
          <Field label="Avg Response Time" value={perf.averageResponseTime != null ? `${perf.averageResponseTime} hrs` : null} />
          <Field label="Completion Rate" value={perf.completionRate != null ? `${perf.completionRate}%` : null} />
          <Field label="Deals Completed" value={perf.dealsCompleted} />
        </dl>
      </Section>

      {svc.notes && (
        <Section title="Notes">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{svc.notes}</p>
        </Section>
      )}
    </div>
  );
};

export default ProfessionalServiceDetails;
